import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Check, Keyboard, Mic, Target } from 'lucide-react';

import { Button } from 'components/ui/button';
import { Card, CardContent, CardFooter, CardTitle } from 'components/ui/card';
import { PersonaPortrait } from './PersonaPortrait';
import type { RoleplayMode, RoleplayScenario } from './types';

type MicStatus = 'unchecked' | 'ok' | 'unavailable';

export const PrebriefScreen: React.FC<{
  scenario: RoleplayScenario;
  processing: boolean;
  error?: string | null;
  onStart: (mode: RoleplayMode) => void;
  onBack?: () => void;
  onStartGuidedVoice?: () => void;
  /** The scorecard skill chosen with "Practice this skill". */
  focus?: { label: string; next: string } | null;
  /** Arrived from the debrief: start at the top with focus on the new screen. */
  returning?: boolean;
}> = ({ scenario, processing, error, onStart, onBack, onStartGuidedVoice, focus = null, returning = false }) => {
  const [micStatus, setMicStatus] = useState<MicStatus>('unchecked');
  const titleRef = useRef<HTMLDivElement>(null);
  const focusNoteRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!returning) return;
    // The debrief was scrolled; the prebrief must not inherit that offset.
    if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
    (focusNoteRef.current ?? titleRef.current)?.focus({ preventScroll: true });
    // Only on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkMic = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      setMicStatus('ok');
    } catch {
      setMicStatus('unavailable');
    }
  };

  return (
    <div className="rp-prebrief-shell flex min-h-screen flex-col items-center justify-center gap-4 px-4 pb-10 pt-7" data-testid="roleplay-prebrief">
      {onBack ? (
        <div className="w-full max-w-[40rem]">
          <Button variant="ghost" size="auto" onClick={onBack} data-testid="roleplay-back">
            <ArrowLeft size={16} /> Back
          </Button>
        </div>
      ) : null}
      <Card variant="glass" disableAnimation maxWidth="large" className="rp-prebrief-card max-w-[40rem]">
        <div className="flex flex-col items-center gap-1 pt-2">
          <div className="rp-prebrief-portrait h-[6.75rem] w-[6.75rem] overflow-hidden rounded-full border border-white/70 bg-white/45">
            <PersonaPortrait persona={scenario.persona} className="h-full w-full" />
          </div>
          <p className="type-body-s mt-2 text-foundation-base-darkbase-dark-400">
            Practice with {scenario.persona.name}
          </p>
        </div>
        <div ref={titleRef} tabIndex={-1} className="outline-none" data-testid="roleplay-prebrief-title">
          <CardTitle title={scenario.title} subtitle={scenario.subtitle} />
        </div>
        <CardContent variant="form">
          <div className="rounded-xl bg-white/60 px-4 py-3">
            <p className="type-body-s-semibold mb-1 text-foundation-base-darkbase-dark-700">The situation</p>
            <p className="type-body-s text-foundation-base-darkbase-dark-500">{scenario.situation}</p>
            <p className="type-body-s mt-2 text-foundation-base-darkbase-dark-400">
              Your role: {scenario.learner_role}
            </p>
          </div>
          {focus ? (
            <div ref={focusNoteRef} tabIndex={-1} className="flex items-start gap-2.5 rounded-xl bg-foundation-orange-50 px-4 py-3 outline-none" data-testid="roleplay-practice-focus">
              <Target size={16} aria-hidden="true" className="mt-0.5 shrink-0 text-foundation-orange-500" />
              <div>
                <p className="type-body-s-semibold text-foundation-base-darkbase-dark-700">Your focus this time: {focus.label}</p>
                {focus.next ? <p className="type-body-s text-foundation-base-darkbase-dark-500">{focus.next}</p> : null}
              </div>
            </div>
          ) : null}
          <div>
            <p className="type-body-s-semibold mb-1 text-foundation-base-darkbase-dark-700">What to practice</p>
            <ul className="list-disc space-y-1 pl-5 text-sm text-foundation-base-darkbase-dark-500">
              {scenario.learning_objectives.map((objective) => <li key={objective}>{objective}</li>)}
            </ul>
          </div>
          <div className="flex items-center justify-between gap-3 rounded-xl bg-white/55 px-3.5 py-2.5">
            <div className="flex items-center gap-2.5">
              <Mic size={18} className="shrink-0 text-foundation-base-darkbase-dark-400" />
              <span className="type-body-s text-foundation-base-darkbase-dark-500" data-testid="roleplay-mic-status">
                {micStatus === 'ok' && 'Microphone is working'}
                {micStatus === 'unchecked' && 'Voice is recommended; test your microphone if you like'}
                {micStatus === 'unavailable' && 'Microphone unavailable — you can type instead'}
              </span>
            </div>
            {micStatus === 'ok' ? <Check size={18} className="shrink-0 text-foundation-orange-500" /> : (
              <Button variant="secondary" size="auto" className="min-h-0 whitespace-nowrap px-3.5 py-1.5 text-[0.8125rem]" onClick={checkMic} data-testid="roleplay-mic-check">
                Test mic
              </Button>
            )}
          </div>
        </CardContent>
        {error && <p role="alert" className="type-body-s mt-3 text-foundation-base-darkbase-dark-700" data-testid="roleplay-start-error">{error}</p>}
        <CardFooter className="grid w-full grid-cols-2 gap-2 p-0 pt-5">
          {onStartGuidedVoice && (
            <div className="col-span-2 flex flex-col gap-2 pb-2">
              <Button className="w-full" onClick={onStartGuidedVoice} disabled={processing} data-testid="roleplay-start-guided-voice">
                <Mic size={18} /> Start guided Voice Mode
              </Button>
              <p className="type-body-s text-center text-foundation-base-darkbase-dark-400">
                Hear the situation, practice, and feedback through a spoken conversation.
              </p>
            </div>
          )}
          <Button className="w-full px-3" onClick={() => onStart('voice')} disabled={processing || micStatus === 'unavailable'} data-testid="roleplay-begin">
            <Mic size={18} /> {processing ? 'Starting…' : 'Begin with voice'}
          </Button>
          <Button className="w-full px-3" variant="secondary" onClick={() => onStart('text')} disabled={processing} data-testid="roleplay-begin-text">
            <Keyboard size={18} /> Begin with text
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};
